import { useState } from "react";

interface CategoryFiltersProps {
  sort: "latest" | "hot";
  onSortChange: (value: "latest" | "hot") => void;
  typeTags: string[];
  activeType: string;
  onTypeChange: (value: string) => void;
  eraTags?: string[];
  activeEra?: string;
  onEraChange?: (value: string) => void;
  onReset: () => void;
  hasFilter: boolean;
}

export default function CategoryFilters({
  sort,
  onSortChange,
  typeTags,
  activeType,
  onTypeChange,
  eraTags = [],
  activeEra = "all",
  onEraChange,
  onReset,
  hasFilter,
}: CategoryFiltersProps) {
  const [open, setOpen] = useState(false);

  const chipClass = (active: boolean) =>
    `cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
      active
        ? "border-primary-500 bg-primary-500 text-background-50"
        : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
    }`;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between p-4 lg:hidden"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-equalizer-line text-primary-600" />
          筛选与排序
        </span>
        <i className={open ? "ri-arrow-up-s-line text-xl" : "ri-arrow-down-s-line text-xl"} />
      </button>

      <div className={`${open ? "block" : "hidden"} border-t border-background-100 p-4 lg:block lg:border-t-0`}>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
            <i className="ri-equalizer-line hidden text-primary-600 lg:inline" />
            排序方式
          </h3>
          {hasFilter && (
            <button
              type="button"
              onClick={onReset}
              className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-500 transition-colors hover:text-primary-700"
            >
              <i className="ri-refresh-line" />
              重置
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-col gap-2">
          {[
            { key: "latest" as const, label: "最新发布", icon: "ri-time-line" },
            { key: "hot" as const, label: "按热度排序", icon: "ri-fire-line" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onSortChange(item.key)}
              className={`flex items-center justify-between rounded-md border px-3 py-2.5 text-sm font-medium transition-colors ${
                sort === item.key
                  ? "border-primary-300 bg-primary-50 text-primary-700"
                  : "border-background-200 text-foreground-700 hover:border-primary-200 hover:text-primary-700"
              }`}
            >
              <span className="flex items-center gap-2">
                <i className={item.icon} />
                {item.label}
              </span>
              {sort === item.key && <i className="ri-check-line text-base" />}
            </button>
          ))}
        </div>

        <div className="mt-6">
          <h3 className="text-sm font-semibold text-foreground-900">内容类型</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={chipClass(activeType === "all")} onClick={() => onTypeChange("all")}>
              全部
            </button>
            {typeTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={chipClass(activeType === tag)}
                onClick={() => onTypeChange(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {eraTags.length > 0 && onEraChange && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-foreground-900">年代</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className={chipClass(activeEra === "all")}
                onClick={() => onEraChange("all")}
              >
                全部
              </button>
              {eraTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className={chipClass(activeEra === tag)}
                  onClick={() => onEraChange(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}